<template>
    <div class="card shadow-sm search-card">
        <div class="card-body">
            <div>
                <div class="row g-2 mb-2">
                    <div :class="isSearchDemo ? 'col-md-6' : 'col-md-4'">
                        <input
                            type="text"
                            class="input filter-input"
                            placeholder="Part (optional)"
                            v-model="formData.part"
                            @input="formData.part = $event.target.value.toUpperCase()"
                        />
                    </div>
                    <div :class="isSearchDemo ? 'col-md-6' : 'col-md-4'">
                        <input
                            type="text"
                            inputmode="numeric"
                            pattern="[0-9]*"
                            oninput="this.value = this.value.replace(/[^0-9]/g, '').replace(/(\..*?)\..*/g, '$1');"
                            class="input filter-input"
                            placeholder="ID (optional)"
                            v-model="formData.sa_id"
                        />
                    </div>
                    <div v-if="!isSearchDemo" class="col-md-4">
                        <input
                            type="text"
                            class="input filter-input"
                            placeholder="Reference (optional)"
                            v-model="formData.customer_ref"
                        />
                    </div>
                </div>

                <div class="row g-2 mb-2">
                    <div v-if="!isSearchDemo" class="col-md-4">
                        <select
                            id="search_customer"
                            class="form-select filter-select"
                            v-model="formData.customer"
                        >
                            <option disabled hidden value="" style="color: var(--color-text-muted)">
                                Customer (optional)
                            </option>
                            <option value="">All Customers</option>
                            <option
                                v-for="item in customer_items"
                                :key="'cust_' + item"
                                :value="item"
                            >
                                {{ item }}
                            </option>
                        </select>
                    </div>
                    <div :class="isSearchDemo ? 'col-md-6' : 'col-md-4'">
                        <select
                            id="date_range"
                            class="form-select filter-select"
                            v-model="formData.date_range"
                            @change="changeDate(formData.date_range)"
                        >
                            <option disabled hidden value="" style="color: var(--color-text-muted)">
                                Date Presets
                            </option>
                            <option
                                :value="element.value"
                                v-for="(element, index) in dateIntervalGroups"
                                :key="index"
                            >
                                {{ element.label }}
                            </option>
                            <option disabled hidden value="custom">Custom</option>
                        </select>
                    </div>
                    <div :class="isSearchDemo ? 'col-md-6' : 'col-md-4'">
                        <VueDatePicker v-model="formData.range" :enable-time-picker="false" range />
                    </div>
                </div>

                <div class="row g-2 controls-row">
                    <div
                        class="col-6 col-lg-auto d-flex align-items-center gap-2 toggle-pill"
                        v-if="shouldShowUnassignedToggle"
                    >
                        <input
                            id="switch_unassigned"
                            type="checkbox"
                            name="switch_unassigned"
                            class="switch is-rounded is-info"
                            v-model="formData.unassigned"
                            @change="handleUnassignedToggle"
                        />
                        <label for="switch_unassigned" class="toggle-label"> Unassigned </label>
                    </div>

                    <div
                        v-if="!isSearchDemo"
                        class="col-6 col-lg-auto d-flex align-items-center gap-2 toggle-pill"
                    >
                        <input
                            id="switch_my_all"
                            type="checkbox"
                            name="switch_my_all"
                            class="switch is-rounded is-info"
                            v-model="formData.my_quotes"
                            :disabled="disableOtherToggles"
                        />
                        <label v-if="formData.my_quotes" for="switch_my_all" class="toggle-label">
                            My Quotes
                        </label>
                        <label v-else for="switch_my_all" class="toggle-label"> Everyone </label>
                    </div>

                    <div
                        v-if="!isSearchDemo"
                        class="col-6 col-lg-auto d-flex align-items-center gap-2 toggle-pill"
                    >
                        <input
                            id="switch_unfinished_all"
                            type="checkbox"
                            name="switch_my_all"
                            class="switch is-rounded is-info"
                            v-model="formData.all_quotes"
                            :disabled="disableOtherToggles"
                        />
                        <label
                            v-if="formData.all_quotes"
                            for="switch_unfinished_all"
                            class="toggle-label"
                        >
                            All Quotes
                        </label>
                        <label v-else for="switch_unfinished_all" class="toggle-label">
                            Unfinished
                        </label>
                    </div>

                    <div
                        class="col-6 col-lg-auto d-flex align-items-center gap-2 toggle-pill"
                        v-if="
                            !isSearchDemo &&
                            (is_staff || dashboard_permissions.includes('myquotes_research'))
                        "
                    >
                        <input
                            id="switch_standard"
                            type="checkbox"
                            name="switch_standard"
                            class="switch is-rounded is-info"
                            v-model="formData.standard_quotes"
                            :disabled="disableOtherToggles"
                        />
                        <label
                            v-if="formData.standard_quotes"
                            for="switch_standard"
                            class="toggle-label"
                        >
                            Standard
                        </label>
                        <label v-else for="switch_standard" class="toggle-label"> Research </label>
                    </div>

                    <div
                        class="col-12 col-lg d-flex justify-content-lg-end justify-content-start gap-2 action-row"
                    >
                        <button @click="reset" class="register-button btn-reset">Reset</button>
                        <button @click="ajaxSearchQuotes" class="login-button btn-search">Search</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import VueDatePicker from '@vuepic/vue-datepicker'
    import { getDates } from '@/helpers/dates.js'
    import { mapStores } from 'pinia'
    import { useQuoteFormStore } from '@/stores/quoteFormStore'
    import { useDropdownsStore } from '@/stores/dropdownsStore'
    import config from '@/services/config'

    export default {
        name: 'QuoteFilter',
        components: {
            VueDatePicker,
        },
        computed: {
            ...mapStores(useQuoteFormStore, useDropdownsStore),
            formData() {
                return this.quoteFormStore.formData
            },
            customer_items() {
                const customers = this.dropdownsStore.customer || []
                return customers.length ? customers : []
            },
            currentSubdomain() {
                return config.getSubdomain ? config.getSubdomain() : ''
            },
            shouldShowUnassignedToggle() {
                return this.currentSubdomain === 'retail'
            },
            isSearchDemo() {
                return config.isSearchDemo?.() ?? false
            },
            disableOtherToggles() {
                return this.shouldShowUnassignedToggle && this.formData.unassigned
            },
        },
        mounted() {
            this.applySearchDemoFilters()
            if (this.formData.unassigned) {
                this.handleUnassignedToggle()
            }
        },
        data() {
            return {
                dashboard_permissions: ['myquotes_research'],
                is_staff: true,

                dateIntervalGroups: [
                    { value: 'today', label: 'Today' },
                    { value: 'week', label: 'Week' },
                    { value: 'this_month', label: 'This Month' },
                    { value: 'prev_month', label: 'Prev Month' },
                    { value: 'this_quarter', label: 'This Quarter' },
                    { value: 'prev_quarter', label: 'Prev Quarter' },
                    { value: 'this_year', label: 'This Year' },
                    { value: 'prev_year', label: 'Prev Year' },
                    { value: 'all', label: 'All' },
                ],
                quoteHeaders: {
                    id: 'ID',
                    customer: 'Customer',
                    quote_type: 'Quote Type',
                    customer_ref: 'Customer Ref',
                    part_number_list: 'Part Number List',
                    part_and_sub_list: 'Part and Sub List',
                    time_created: 'Time Created',
                    search_finished: 'Search Finished',
                    quote_finished: 'Quote Finished',
                    name: 'Name',
                    total_value: 'Total Value',
                },
            }
        },
        methods: {
            getDates,
            changeDate(date_range) {
                let range = this.getDates(date_range)
                if (range) {
                    this.formData.range = range
                }
            },
            reset() {
                this.quoteFormStore.resetFormData()
                this.applySearchDemoFilters()
                this.handleUnassignedToggle()

                this.$emit('reset-quotes')
            },
            triggerResetShortcut() {
                this.reset()
            },
            async ajaxSearchQuotes(e) {
                e?.preventDefault?.()
                this.$emit('search-quotes', this.formData)
            },
            triggerSearchShortcut() {
                this.$emit('search-quotes', this.formData)
            },
            handleUnassignedToggle() {
                if (this.formData.unassigned) {
                    this.formData.my_quotes = false
                    this.formData.all_quotes = true
                    this.formData.standard_quotes = true
                }
            },
            applySearchDemoFilters() {
                if (!this.isSearchDemo) return
                this.formData.my_quotes = true
                this.formData.standard_quotes = false
            },
        },
    }
</script>

<style scoped>
    .search-card {
        max-width: 980px;
        margin: 0 auto;
        border: 1px solid var(--color-border-default);
        border-top: 4px solid var(--color-brand-primary);
        border-radius: var(--radius-md);
        box-shadow: var(--shadow-md);
        background: var(--color-surface-canvas);
        color: var(--color-text-primary);
    }

    .search-card :is(.card-body, .card) {
        background: var(--color-surface-canvas);
        color: var(--color-text-primary);
    }

    .card-body {
        padding: 0.95rem 1rem;
    }

    .filter-input,
    .filter-select {
        min-height: 42px;
        border: 1px solid var(--color-border-default);
        border-radius: var(--radius-sm);
        background: var(--color-surface-canvas);
        font-size: 0.97rem;
        color: var(--color-text-primary);
        transition: border-color 0.15s ease, box-shadow 0.15s ease;
    }

    .filter-input::placeholder {
        color: var(--color-text-muted);
    }

    .filter-input:focus,
    .filter-select:focus {
        border-color: var(--color-border-accent-strong);
        box-shadow: var(--shadow-focus-brand);
        outline: none;
    }

    .controls-row {
        margin-top: 0.1rem;
    }

    .toggle-pill {
        min-height: 40px;
        border: 1px solid var(--color-border-default);
        border-radius: var(--radius-pill);
        background: var(--color-surface-canvas);
        padding: 0.32rem 0.75rem;
    }

    .toggle-label {
        margin: 0;
        outline: none;
        font-size: 0.94rem;
        font-weight: 500;
        color: var(--color-text-primary);
        user-select: none;
        white-space: nowrap;
    }

    .action-row {
        margin-top: 0.2rem;
    }

    .btn-reset,
    .btn-search {
        min-height: 40px;
        min-width: 102px;
        border-radius: 8px;
        font-size: 0.95rem;
        font-weight: 700;
        letter-spacing: 0.01em;
        padding: 0.45rem 1rem;
    }

    .btn-reset {
        border-color: #cfd4dd;
    }

    .btn-reset:hover {
        background: #f2f4f8;
    }

    .btn-search {
        background: var(--color-brand-primary);
        color: var(--color-text-inverse);
        border-color: var(--color-brand-primary);
    }

    .btn-search:hover {
        background: var(--color-brand-primary-strong);
        border-color: var(--color-brand-primary-strong);
    }

    :deep(.switch[type='checkbox'].is-info:checked + label::before),
    :deep(.switch[type='checkbox'].is-info:checked + label:before) {
        background: var(--color-brand-primary);
    }

    :deep(.dp__input) {
        min-height: 42px;
        border-radius: var(--radius-sm);
        border: 1px solid var(--color-border-default);
        color: var(--color-text-primary);
        font-size: 0.97rem;
        background: var(--color-surface-canvas);
    }

    :deep(.dp__input:focus) {
        border-color: var(--color-border-accent-strong);
        box-shadow: var(--shadow-focus-brand);
    }

    :deep(.dp__input::placeholder) {
        color: var(--color-text-muted);
    }

    :deep(.dp__menu),
    :deep(.dp__calendar),
    :deep(.dp__outer_menu_wrap) {
        background: var(--color-surface-canvas);
        color: var(--color-text-primary);
        border-color: var(--color-border-default);
    }

    :deep(.dp__calendar_header),
    :deep(.dp__month_year_wrap),
    :deep(.dp__arrow_top) {
        background: var(--color-surface-canvas);
        color: var(--color-text-primary);
    }

    :deep(.dp__cell_inner),
    :deep(.dp__calendar_item) {
        color: var(--color-text-primary);
    }

    :deep(.dp__action_button),
    :deep(.dp__action_row),
    :deep(.dp__selection_preview) {
        color: var(--color-text-primary);
        background: var(--color-surface-canvas);
    }

    html[data-theme='dark'] :deep(.dp__input_icon),
    html[data-theme='dark'] :deep(.dp__clear_icon),
    html[data-theme='dark'] :deep(.dp__icon) {
        color: var(--color-text-muted);
    }

    @media (max-width: 767px) {
        .card-body {
            padding: 0.85rem;
        }

        .toggle-pill {
            border-radius: 12px;
        }

        .action-row {
            margin-top: 0;
        }
    }
</style>
